/**
 * Build a link into a list page with its filters already applied.
 *
 * The list pages read their filters from `filter_<ColumnName>` query params, and `SearchFilter`
 * writes them **double-encoded**: it calls `encodeURIComponent` on the value and then hands the
 * result to `URLSearchParams`, which encodes the `%` signs again. The read side matches, doing
 * `searchParams.get()` (one decode) followed by `decodeURIComponent` (the second).
 *
 * Anything hand-building such a URL has to encode twice too, and the two links that did -- "View
 * this company's jobs" on the company and employer workspaces -- did neither. They linked to
 * `/jobs?company=<name>`, a parameter no page has ever read, so the Jobs list opened completely
 * unfiltered while the URL looked like it had asked for something. Nothing errored; the filter
 * simply was not there.
 *
 * The column name is the display label from the page's own column map (`Company`, `Job Status`),
 * not the GraphQL field, because that is what SearchFilter round-trips.
 */
export function filteredListPath(
  basePath: string,
  filters: Record<string, string>,
): string {
  const params = new URLSearchParams();
  for (const [column, value] of Object.entries(filters)) {
    if (!value?.trim()) continue;
    // Encoded here, and again by URLSearchParams. See the note above.
    params.set(`filter_${column}`, encodeURIComponent(value));
  }
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

/**
 * Jobs belonging to one company.
 *
 * By id, never by name: two companies sharing a name, or a rename between the link being built
 * and followed, and a name-keyed filter confidently lists somebody else's roles.
 */
export const companyJobsPath = (companyId: string): string =>
  filteredListPath('/jobs', { 'Company ID': companyId });

/**
 * The applications list, pre-filtered to one job.
 *
 * Not a `filter_<Column>` link: JobApplicants reads `?jobId=` and `?jobLabel=` directly, singly
 * encoded, to seed its job chip. `jobLabel` is only the text on that chip -- the filter itself is
 * the id -- so a stale or missing label degrades the display and never the result.
 */
export function jobApplicantsPath(jobId: string, jobLabel?: string): string {
  const params = new URLSearchParams({ jobId });
  if (jobLabel?.trim()) params.set('jobLabel', jobLabel);
  return `/applications/list?${params.toString()}`;
}

/**
 * "no applicants" / "1 applicant" / "N applicants".
 *
 * Zero is stated rather than left blank. On a picker row that moves a job between employers, an
 * empty space means "we did not count", and "no applicants" means "safe to move" -- opposite
 * conclusions from the same gap.
 */
export function applicantLabel(count: number): string {
  if (count === 0) return 'no applicants';
  return count === 1 ? '1 applicant' : `${count} applicants`;
}
